/**
 * SEMANTIC TAGS ENGINE - Motor de búsqueda semántica por tags
 * 
 * Este servicio permite:
 * 1. Generar embeddings para los tags de un documento
 * 2. Buscar documentos por similitud semántica de tags
 * 3. Pre-filtrar documentos antes de aplicar búsqueda vectorial de contenido
 * 
 * ARQUITECTURA:
 * - Al indexar: genera embedding combinado de todos los tags del documento
 * - Al buscar: compara embedding de query con tagsEmbedding de documentos
 * - Resultado: pre-filtrado semántico eficiente
 * 
 * USO:
 * ```ts
 * // Indexar tags de un documento
 * await semanticTagsEngine.indexDocumentTags({
 *   id: 'chunk123',
 *   collectionName: 'therapist_note_chunks',
 *   semanticTags: ['reconciliación', 'compasión', 'liberación'],
 * });
 * 
 * // Buscar documentos por query
 * const results = await semanticTagsEngine.searchBySemanticTags(
 *   'therapist_note_chunks',
 *   'perdón',
 *   { ownerId: 'therapist123', ownerField: 'therapistId' }
 * );
 * // → Encuentra documentos con tags semánticamente similares a "perdón"
 * ```
 */

import { db } from '@/core/config/firebase';
import { Logger } from '@/core/utils';
import { embeddingService } from '../indexing/embedding-service';
import {
    TaggableDocument,
    TagIndexingResult,
    BatchTagIndexingResult,
    TagSearchOptions,
    TagSearchResult,
    TAG_EMBEDDING_CONFIG,
} from './types';

const logger = Logger.getLogger('SemanticTagsEngine');

// ============================================
// SEMANTIC TAGS ENGINE
// ============================================

class SemanticTagsEngine {

    /**
     * Generar e indexar embedding de tags para un documento
     */
    async indexDocumentTags(doc: TaggableDocument): Promise<TagIndexingResult> {
        const startTime = Date.now();

        try {
            // Validar que hay tags
            if (!doc.semanticTags || doc.semanticTags.length === 0) {
                return {
                    documentId: doc.id,
                    success: false,
                    error: 'No semantic tags to index',
                    processingTime: Date.now() - startTime,
                };
            }

            // Limitar número de tags
            const tagsToEmbed = doc.semanticTags.slice(0, TAG_EMBEDDING_CONFIG.maxTagsForEmbedding);

            // Combinar tags en un texto para generar embedding único
            // Formato: "tema: tag1, tag2, tag3" mejora la calidad del embedding
            const tagsText = `Temas: ${tagsToEmbed.join(', ')}`;

            // Generar embedding
            const result = await embeddingService.generateEmbedding({
                text: tagsText,
            });

            // Guardar en Firestore
            await db.collection(doc.collectionName).doc(doc.id).update({
                tagsEmbedding: result.embedding,
                hasTagsEmbedding: true,
                tagsEmbeddingUpdatedAt: new Date().toISOString(),
            });

            logger.debug('Document tags indexed', {
                documentId: doc.id,
                collection: doc.collectionName,
                tagsCount: tagsToEmbed.length,
            });

            return {
                documentId: doc.id,
                success: true,
                tagsEmbedding: result.embedding,
                processingTime: Date.now() - startTime,
            };

        } catch (error: any) {
            logger.error('Failed to index document tags', error, {
                documentId: doc.id
            });

            return {
                documentId: doc.id,
                success: false,
                error: error.message,
                processingTime: Date.now() - startTime,
            };
        }
    }

    /**
     * Indexar tags de múltiples documentos en batch
     */
    async indexBatchTags(documents: TaggableDocument[]): Promise<BatchTagIndexingResult> {
        const startTime = Date.now();
        const results: TagIndexingResult[] = [];
        let successful = 0;
        let failed = 0;

        // Procesar en batches de 5 para no saturar
        const BATCH_SIZE = 5;

        for (let i = 0; i < documents.length; i += BATCH_SIZE) {
            const batch = documents.slice(i, i + BATCH_SIZE);

            const batchResults = await Promise.allSettled(
                batch.map(doc => this.indexDocumentTags(doc))
            );

            for (const result of batchResults) {
                if (result.status === 'fulfilled') {
                    results.push(result.value);
                    if (result.value.success) successful++;
                    else failed++;
                } else {
                    failed++;
                    results.push({
                        documentId: 'unknown',
                        success: false,
                        error: result.reason?.message || 'Unknown error',
                        processingTime: 0,
                    });
                }
            }

            // Pausa entre batches
            if (i + BATCH_SIZE < documents.length) {
                await this.sleep(100);
            }
        }

        const totalTime = Date.now() - startTime;

        logger.info('Batch tag indexing completed', {
            total: documents.length,
            successful,
            failed,
            totalTime: `${totalTime}ms`,
        });

        return { successful, failed, results, totalTime };
    }

    /**
     * Buscar documentos por similitud semántica de tags
     * 
     * Esto permite encontrar documentos cuyos tags son semánticamente
     * similares a la query, incluso si no hay coincidencia exacta.
     */
    async searchBySemanticTags<T = unknown>(
        collectionName: string,
        query: string,
        options: TagSearchOptions = {}
    ): Promise<TagSearchResult<T>[]> {
        const startTime = Date.now();
        const {
            ownerId,
            ownerField = TAG_EMBEDDING_CONFIG.supportedCollections[collectionName],
            limit = TAG_EMBEDDING_CONFIG.maxPreFilterResults,
            similarityThreshold = TAG_EMBEDDING_CONFIG.defaultSimilarityThreshold,
        } = options;

        try {
            // 1. Generar embedding de la query
            const queryResult = await embeddingService.generateEmbedding({
                text: `Tema: ${query}`,  // Mismo formato que al indexar
            });
            const queryEmbedding = queryResult.embedding;

            // 2. Obtener documentos con tagsEmbedding
            let firestoreQuery: FirebaseFirestore.Query = db.collection(collectionName)
                .where('hasTagsEmbedding', '==', true);

            // Filtrar por propietario si se especifica
            if (ownerId && ownerField) {
                firestoreQuery = firestoreQuery.where(ownerField, '==', ownerId);
            }

            // Limitar para performance
            firestoreQuery = firestoreQuery.limit(200);

            const snapshot = await firestoreQuery.get();

            if (snapshot.empty) {
                logger.info('No documents with tags embedding found', {
                    collectionName,
                    ownerId,
                });
                return [];
            }

            // 3. Calcular similitud para cada documento
            const results: TagSearchResult<T>[] = [];

            for (const doc of snapshot.docs) {
                const data = doc.data();
                const tagsEmbedding = data.tagsEmbedding as number[] | undefined;

                if (!tagsEmbedding || tagsEmbedding.length === 0) continue;

                // Calcular similitud coseno
                const similarity = embeddingService.cosineSimilarity(
                    queryEmbedding,
                    tagsEmbedding
                );

                // Solo incluir si supera el umbral
                if (similarity >= similarityThreshold) {
                    results.push({
                        document: { id: doc.id, ...data } as T,
                        documentId: doc.id,
                        tagSimilarity: similarity,
                        matchedTags: data.semanticTags,
                    });
                }
            }

            // 4. Ordenar por similitud
            results.sort((a, b) => b.tagSimilarity - a.tagSimilarity);

            // 5. Limitar resultados
            const finalResults = results.slice(0, limit);

            const elapsed = Date.now() - startTime;
            logger.info('Semantic tag search completed', {
                query: query.substring(0, 30),
                collectionName,
                scanned: snapshot.size,
                matches: results.length,
                returned: finalResults.length,
                topSimilarity: finalResults[0]?.tagSimilarity.toFixed(3),
                elapsed: `${elapsed}ms`,
            });

            return finalResults;

        } catch (error: any) {
            logger.error('Semantic tag search failed', error, {
                collectionName,
                query: query.substring(0, 30),
            });
            throw error;
        }
    }

    /**
     * Pre-filtrar documentos por tags antes de búsqueda vectorial de contenido
     * 
     * Este método es el corazón del pre-filtrado semántico:
     * 1. Busca documentos con tags similares a la query
     * 2. Retorna solo los IDs para que luego se aplique búsqueda por contenido
     */
    async preFilterByTags(
        collectionName: string,
        query: string,
        options: TagSearchOptions = {}
    ): Promise<string[]> {
        const results = await this.searchBySemanticTags(
            collectionName,
            query,
            {
                ...options,
                limit: options.limit || TAG_EMBEDDING_CONFIG.maxPreFilterResults,
            }
        );

        return results.map(r => r.documentId);
    }

    /**
     * Obtener estadísticas de indexación de tags
     */
    async getTagIndexingStats(
        collectionName: string,
        ownerId?: string
    ): Promise<{
        total: number;
        indexed: number;
        pending: number;
        percentage: number;
    }> {
        const ownerField = TAG_EMBEDDING_CONFIG.supportedCollections[collectionName];

        let baseQuery: FirebaseFirestore.Query = db.collection(collectionName);
        if (ownerId && ownerField) {
            baseQuery = baseQuery.where(ownerField, '==', ownerId);
        }

        const [totalSnapshot, indexedSnapshot] = await Promise.all([
            baseQuery.get(),
            baseQuery.where('hasTagsEmbedding', '==', true).get(),
        ]);

        const total = totalSnapshot.size;
        const indexed = indexedSnapshot.size;

        return {
            total,
            indexed,
            pending: total - indexed,
            percentage: total > 0 ? Math.round((indexed / total) * 100) : 100,
        };
    }

    /**
     * Indexar todos los documentos pendientes de una colección
     */
    async indexPendingTags(
        collectionName: string,
        ownerId?: string,
        limit: number = 50
    ): Promise<BatchTagIndexingResult> {
        const ownerField = TAG_EMBEDDING_CONFIG.supportedCollections[collectionName];

        // Buscar documentos sin tagsEmbedding pero CON semanticTags
        let query: FirebaseFirestore.Query = db.collection(collectionName)
            .where('hasTagsEmbedding', '!=', true)
            .limit(limit);

        if (ownerId && ownerField) {
            query = query.where(ownerField, '==', ownerId);
        }

        const snapshot = await query.get();

        if (snapshot.empty) {
            logger.info('No pending documents to index', { collectionName, ownerId });
            return { successful: 0, failed: 0, results: [], totalTime: 0 };
        }

        // Filtrar solo los que tienen semanticTags
        const documents: TaggableDocument[] = snapshot.docs
            .map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    collectionName,
                    semanticTags: data.semanticTags || [],
                    hasTagsEmbedding: false,
                    ownerId: data[ownerField],
                };
            })
            .filter(doc => doc.semanticTags.length > 0);

        if (documents.length === 0) {
            return { successful: 0, failed: 0, results: [], totalTime: 0 };
        }

        logger.info('Starting pending tag indexing', {
            collectionName,
            documentsToIndex: documents.length,
        });

        return this.indexBatchTags(documents);
    }

    // ============================================
    // UTILIDADES PRIVADAS
    // ============================================

    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

// Singleton export
export const semanticTagsEngine = new SemanticTagsEngine();
export default semanticTagsEngine;

